import { css } from 'astroturf';
import Prism from 'prismjs';
import React, { useEffect, useRef, useState } from 'react';
import {
  Activity,
  Archive,
  Bell,
  Bookmark,
  Calendar,
  Camera,
  Clock,
  Fire,
  Location,
  Mail,
  Music,
  Photo,
  Star,
  Tag,
  User,
} from 'react-bytesize-icons';

const CONTENTHEIGHT = 480;

const LAYOUTSTRIPE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <defs>
      <pattern id="pinstripe" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(-45)">
        <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(25,32,255,0.8)" stroke-width="1" stroke-dasharray="7 3"></line>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#pinstripe)"></rect>
  </svg>`;
const LAYOUTSTRIPE = `url(data:image/svg+xml;base64,${btoa(LAYOUTSTRIPE_SVG)})`;

const INSET_NOTCH_TOP = 44;
const INSET_NOTCH_RIGHT = 30;
const INSET_ISLAND_TOP = 44;
const INSET_ISLAND_RIGHT = 44;
const INSET_PUNCH_TOP = 32;
const INSET_PUNCH_RIGHT = 32;
const DEFAULT_BOTTOM = 34;
const ACTION_BAR_HEIGHT = 18;
const ADDRESS_BAR_HEIGHT = 36;

function cutoutTopInset(type) {
  if (type === 'island') return INSET_ISLAND_TOP;
  if (type === 'punch') return INSET_PUNCH_TOP;
  if (type === 'none') return 0;
  return INSET_NOTCH_TOP;
}

function cutoutRightInset(type) {
  if (type === 'island') return INSET_ISLAND_RIGHT;
  if (type === 'punch') return INSET_PUNCH_RIGHT;
  if (type === 'notch') return INSET_NOTCH_RIGHT;
  return 0;
}

const styles = css`
  .demo {
    background: linear-gradient(135deg, rgba(34, 95, 197, 0.16), rgba(31, 160, 122, 0.16));
    border-radius: 16px;
    box-shadow: rgba(10, 37, 99, 0.2) 0.3em 0.3em 2em inset;
    padding: 1.5rem;
    margin: 2rem 0;
  }

  .controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.9rem;
    margin-bottom: 1rem;
    align-items: end;
  }

  .playgroundLayout {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 1.5rem;
    align-items: start;
  }

  @media (max-width: 560px) {
    .playgroundLayout {
      grid-template-columns: 1fr;
    }
  }

  .playgroundControls {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .playgroundPhoneCol {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .phoneColToolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2rem;
  }

  .playgroundToolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  .playgroundToolbarLeft {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #355;
  }

  .playgroundToolbarRight {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .rotateButton {
    background: rgba(255, 255, 255, 0.6);
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    border-radius: 8px;
    padding: 0.4rem 0.7rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    font-weight: 700;
    color: #16335c;
    transition: background 0.15s;

    &:active svg {
      scale: 0.75;
      transform: rotate(-10deg);
    }
  }

  .rotateButton:hover {
    background: rgba(255, 255, 255, 0.85);
  }

  @keyframes spinOneAndHalf {
    from {
      rotate: 0deg;
    }
    to {
      rotate: 360deg;
    }
  }

  .rotateIcon {
    will-change: rotate, scale, transform;
    transition:
      scale 0.15s ease-in-out,
      transform 0.15s ease-in-out;
    animation: spinOneAndHalf 0.5s ease-out both;
  }

  .field .overlayToggle {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    font-size: 0.84rem;
    color: #355;
    text-transform: capitalize;
    font-weight: 500;
  }

  .segmentedToggle {
    display: flex;
    width: 220px;
    border-radius: 8px;
    overflow: hidden;
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    background: rgba(255, 255, 255, 0.6);
  }

  .segmentedToggleBtn {
    padding: 0.4rem 0.7rem;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 700;
    display: flex;
    flex: 1 1 50%;
    justify-content: center;
    align-items: center;
    transition:
      background 0.15s,
      color 0.15s;
    color: rgba(22, 51, 92, 0.45);
    white-space: nowrap;
  }

  .segmentedToggleBtn + .segmentedToggleBtn {
    border-left: 1.5px solid rgba(14, 19, 35, 0.18);
  }

  .segmentedToggleBtnActive.segmentedToggleBtnBuggy {
    background: #b91c3c;
    color: #fff;
  }

  .segmentedToggleBtnActive.segmentedToggleBtnSafe {
    background: #0f63d8;
    color: #fff;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .field label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #355;
  }

  .field input {
    accent-color: #1976d2;
  }

  .field select {
    appearance: auto;
    background: rgba(255, 255, 255, 0.6);
    border: 1.5px solid rgba(14, 19, 35, 0.18);
    border-radius: 8px;
    padding: 0.4rem 0.7rem;
    font-size: 0.8rem;
    font-weight: 700;
    color: #16335c;
    cursor: pointer;
    width: 100%;
  }

  .field select:hover {
    background: rgba(255, 255, 255, 0.85);
  }

  .checkbox {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.88rem;
  }

  .hint {
    font-size: 0.84rem;
    color: #2d4954;
    margin-top: 0.75rem;
    line-height: 1.4;
  }

  .frameGrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
  }

  .frameWrap {
    min-width: 0;
  }

  .frameViewport {
    position: relative;
    width: 100%;
    max-width: 320px;
    aspect-ratio: 9 / 19;
    margin: 0 auto;
  }

  .frameViewportRotated {
    max-width: 675px;
    aspect-ratio: 19 / 9;
  }

  .frameTitle {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #355;
    margin: 0 0 0.5rem;
  }

  .frame {
    --safe-top: 0px;
    --safe-right: 0px;
    --safe-bottom: 0px;
    --safe-left: 0px;
    --safe-max-bottom: 0px;

    position: absolute;
    inset: 0;
    border-radius: 24px;
    overflow: hidden;
    border: 8px solid #0e1323;
    background: #eef5ff;
    box-shadow: 0 14px 36px rgba(6, 17, 43, 0.22);
  }

  .frameRotated {
    border-radius: 20px;
  }

  .screen {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, #f8fbff, #e9f2ff 45%, #f7fff7);
  }

  .overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 4;
  }

  .deviceChrome {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 5;
  }

  .cutout {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background: #090d17;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  }

  .cutoutNotch {
    width: 40%;
    max-width: 136px;
    min-width: 94px;
    height: 30px;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .cutoutIsland {
    top: 8px;
    width: 34%;
    max-width: 120px;
    min-width: 86px;
    height: 26px;
    border-radius: 999px;
  }

  .cutoutPunch {
    top: 8px;
    width: 16px;
    height: 16px;
    border-radius: 999px;
  }

  .actionBar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: var(--action-bar-size, var(--safe-bottom));
    background: rgba(8, 12, 20, 0.6);
    backdrop-filter: blur(2px);
  }

  .actionHandle {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc((var(--action-bar-size, var(--safe-bottom)) - 4px) / 2);
    width: 34%;
    max-width: 108px;
    min-width: 72px;
    height: 4px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
  }

  .browserBar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: var(--action-bar-size, var(--safe-bottom));
    height: var(--browser-bar-size);
    background: rgba(14, 18, 30, 0.92);
    border-top: 1px solid rgba(255, 255, 255, 0.16);
  }

  .browserBarBody {
    margin: 7px 12px;
    height: calc(100% - 14px);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.25);
  }

  .deviceChromeRotated .cutoutNotch {
    top: 50%;
    left: auto;
    right: 0;
    transform: translateY(-50%);
    width: 30px;
    height: 40%;
    min-height: 80px;
    max-height: 160px;
    min-width: 30px;
    max-width: 30px;
    border-bottom-right-radius: 0;
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
    border-top-right-radius: 0;
  }

  .deviceChromeRotated .cutoutIsland {
    top: 50%;
    left: auto;
    right: 8px;
    transform: translateY(-50%);
    width: 26px;
    height: 34%;
    min-height: 72px;
    max-height: 120px;
    min-width: 26px;
    max-width: 26px;
    border-radius: 999px;
  }

  .deviceChromeRotated .cutoutPunch {
    top: 50%;
    left: auto;
    right: 10px;
    bottom: auto;
    transform: translateY(-50%);
  }

  .deviceChromeRotated .browserBar {
    left: 0;
    right: 0;
    bottom: var(--action-bar-size, var(--safe-bottom));
    width: auto;
    height: var(--browser-bar-size);
    border-top: 1px solid rgba(255, 255, 255, 0.16);
    border-left: 0;
  }

  .deviceChromeRotated .browserBarBody {
    margin: 7px 12px;
    width: calc(100% - 24px);
    height: calc(100% - 14px);
  }

  .frameRotated .insetButton {
    left: calc(var(--safe-left) + 12px);
    right: calc(var(--safe-right) + 10px);
    bottom: calc(var(--safe-bottom) + 10px);
  }

  .frameRotated .maxButton {
    left: calc(var(--safe-left) + 12px);
    right: calc(var(--safe-max-bottom) + 10px);
    bottom: calc(var(--safe-bottom) + 10px);
  }

  .frameRotated .footerButton {
    left: calc(var(--safe-left) + 12px);
    right: calc(var(--safe-right) + 10px);
    bottom: calc(var(--safe-bottom) + 10px);
  }

  .frameRotated .chatButtonBad {
    right: var(--chat-offset, 10px);
    bottom: var(--chat-offset, 10px);
  }

  .frameRotated .chatButtonGood {
    right: calc(var(--safe-right) + var(--chat-offset, 10px));
    bottom: calc(var(--safe-bottom) + var(--chat-offset, 10px));
  }

  .frameRotated .insetButtonHalf {
    left: calc(var(--safe-left) + 10px);
  }

  .frameRotated .maxButtonHalf {
    right: calc(var(--safe-right) + 10px);
  }

  .overlayTop,
  .overlayRight,
  .overlayBottom,
  .overlayLeft {
    position: absolute;
    background: rgba(31, 110, 219, 0.28);
    mix-blend-mode: screen;
  }

  .overlayTop {
    top: 0;
    left: 0;
    right: 0;
    height: var(--safe-top);
  }

  .overlayRight {
    top: 0;
    right: 0;
    width: var(--safe-right);
    bottom: 0;
  }

  .overlayBottom {
    left: 0;
    right: 0;
    bottom: 0;
    height: var(--safe-bottom);
  }

  .overlayLeft {
    top: 0;
    left: 0;
    width: var(--safe-left);
    bottom: 0;
  }

  .overlayBottomInset {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: var(--safe-bottom);
    background: rgba(5, 161, 121, 0.38);
    mix-blend-mode: screen;
  }

  .overlayBottomMax {
    position: absolute;
    left: 0;
    right: 0;
    bottom: var(--safe-bottom);
    height: calc(var(--safe-max-bottom) - var(--safe-bottom));
    background: rgba(15, 99, 216, 0.3);
    mix-blend-mode: screen;
  }

  .topBar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: calc(var(--safe-top) + 8px);
    padding-right: calc(var(--safe-right) + 16px);
    padding-left: calc(var(--safe-left) + 16px);
    height: calc(var(--safe-top) + 42px);
    background: #16335c;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    z-index: 2;
  }

  .content {
    flex: 1;
    overflow: hidden;
    padding-top: calc(var(--safe-top) + 52px);
    padding-right: calc(var(--safe-right) + 12px);
    padding-bottom: calc(var(--safe-bottom) + 76px);
    padding-left: calc(var(--safe-left) + 12px);
    color: #1a2d44;
  }

  .card {
    background: #fff;
    border: 1px solid #dce8ff;
    border-radius: 12px;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.7rem;
    box-shadow: 0 4px 18px rgba(18, 44, 89, 0.08);
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .cardIcon {
    flex-shrink: 0;
    color: rgba(22, 51, 92, 0.3);
    display: flex;
    align-items: center;
  }

  .cardLines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  .cardTextBar {
    height: 8px;
    border-radius: 999px;
    background: rgba(22, 51, 92, 0.22);
    width: 75%;
  }

  .cardSubBar {
    height: 6px;
    border-radius: 999px;
    background: rgba(22, 51, 92, 0.1);
    width: 55%;
  }

  .cardSubBar2 {
    height: 6px;
    border-radius: 999px;
    background: rgba(22, 51, 92, 0.1);
    width: 38%;
  }

  .footerButton {
    position: absolute;
    left: calc(var(--safe-left) + 12px);
    right: calc(var(--safe-right) + 12px);
    bottom: calc(var(--safe-bottom) + 10px);
    border: 0;
    border-radius: 999px;
    padding: 0.65rem 0.9rem;
    background: linear-gradient(90deg, #0f63d8, #05a179);
    color: #fff;
    font-weight: 700;
    z-index: 3;
    box-shadow: 0 8px 20px rgba(8, 42, 104, 0.35);
  }

  .maxButton {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: calc(var(--safe-max-bottom) + 10px);
    border: 0;
    border-radius: 10px;
    padding: 0.58rem 0.8rem;
    background: #0f63d8;
    color: #fff;
    font-weight: 700;
    z-index: 3;
    font-size: 0.8rem;
  }

  .insetButton {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: calc(var(--safe-bottom) + 10px);
    border: 0;
    border-radius: 10px;
    padding: 0.58rem 0.8rem;
    background: #05a179;
    color: #fff;
    font-weight: 700;
    z-index: 3;
    font-size: 0.8rem;
  }

  .chatButtonBad,
  .chatButtonGood {
    position: absolute;
    width: 52px;
    height: 52px;
    border: 0;
    border-radius: 999px;
    color: #fff;
    font-weight: 700;
    font-size: 0.72rem;
    z-index: 3;
    box-shadow: 0 8px 20px rgba(8, 42, 104, 0.35);
  }

  .chatButtonBad {
    right: var(--chat-offset, 10px);
    bottom: var(--chat-offset, 10px);
    background: #cc2f5f;
  }

  .chatButtonGood {
    right: calc(var(--safe-right) + var(--chat-offset, 10px));
    bottom: calc(var(--safe-bottom) + var(--chat-offset, 10px));
    background: #0f63d8;
  }

  .insetButtonHalf {
    position: absolute;
    left: 10px;
    width: calc(50% - 16px - var(--safe-right) / 2);
    bottom: calc(var(--safe-bottom) + 10px);
    border: 0;
    border-radius: 10px;
    padding: 0.5rem 0.4rem;
    background: #05a179;
    color: #fff;
    font-weight: 700;
    font-size: 0.7rem;
    z-index: 3;
    text-align: center;
    line-height: 1.2;
  }

  .maxButtonHalf {
    position: absolute;
    right: 10px;
    width: calc(50% - 16px - var(--safe-right) / 2);
    bottom: calc(var(--safe-max-bottom) + 10px);
    border: 0;
    border-radius: 10px;
    padding: 0.5rem 0.4rem;
    background: #0f63d8;
    color: #fff;
    font-weight: 700;
    font-size: 0.7rem;
    z-index: 3;
    text-align: center;
    line-height: 1.2;
  }

  .readout {
    margin-top: 0.9rem;
    background: #121a32;
    color: #d7f0ff;
    border-radius: 10px;
    padding: 0.8rem;
    font-size: 0.78rem;
    line-height: 1.45;
    overflow: auto;
    white-space: pre;
    font-family: 'Operator Mono', 'Fira Code', Consolas, monospace;
  }

  .codeBlockWrap {
    position: relative;
    margin-top: 0.75rem;

    & :global(.gatsby-highlight) {
      margin: 0;
      border-radius: 10px;
      overflow: auto;
      background: #171734;
    }

    & :global(pre[class*='language-']) {
      margin: 0;
      padding: 1.25rem 1.5rem;
      font-size: 0.82rem;
      line-height: 1.5;
      white-space: pre;
    }
  }
`;

function SafeAreaFrame({
  title,
  top,
  right,
  bottom,
  left,
  maxBottom,
  showOverlay,
  buttonType = 'inset',
  topLabel = 'Page title',
  headerUsesInset = true,
  cutoutType = 'notch',
  showActionBar = true,
  showBrowserBar = false,
  browserBarSize = 0,
  rotated = false,
  chatOffset = 10,
  actionBarSize = undefined,
  browserBarSlide = 0,
  browserBarSnapping = false,
  overlayMode = 'standard',
  contentScrollOffset = 0,
  screenStyle = undefined,
  contentScrollable = false,
}) {
  const resolvedActionBarSize = actionBarSize !== undefined ? actionBarSize : bottom;
  return (
    <div className={styles.frameWrap}>
      {title ? <div className={styles.frameTitle}>{title}</div> : null}
      <div className={`${styles.frameViewport} ${rotated ? styles.frameViewportRotated : ''}`}>
        <div
          className={`${styles.frame} ${rotated ? styles.frameRotated : ''}`}
          style={{
            '--safe-top': `${top}px`,
            '--safe-right': `${right}px`,
            '--safe-bottom': `${bottom}px`,
            '--safe-left': `${left}px`,
            '--safe-max-bottom': `${maxBottom}px`,
            '--browser-bar-size': `${browserBarSize}px`,
            '--chat-offset': `${chatOffset}px`,
            '--action-bar-size': `${resolvedActionBarSize}px`,
          }}
        >
          <div className={styles.screen} style={screenStyle}>
            <div
              className={styles.topBar}
              style={
                headerUsesInset
                  ? undefined
                  : {
                      paddingTop: '8px',
                      height: '42px',
                    }
              }
            >
              {topLabel}
            </div>
            <div className={styles.content} style={contentScrollable ? { overflow: 'auto' } : undefined}>
              <div style={contentScrollOffset ? { transform: `translateY(-${contentScrollOffset}px)` } : undefined}>
                {[
                  ['star', <Star width={18} height={18} />],
                  ['bell', <Bell width={18} height={18} />],
                  ['mail', <Mail width={18} height={18} />],
                  ['bookmark', <Bookmark width={18} height={18} />],
                  ['calendar', <Calendar width={18} height={18} />],
                  ['photo', <Photo width={18} height={18} />],
                  ['user', <User width={18} height={18} />],
                  ['clock', <Clock width={18} height={18} />],
                  ['activity', <Activity width={18} height={18} />],
                  ['archive', <Archive width={18} height={18} />],
                  ['camera', <Camera width={18} height={18} />],
                  ['fire', <Fire width={18} height={18} />],
                  ['location', <Location width={18} height={18} />],
                  ['music', <Music width={18} height={18} />],
                  ['tag', <Tag width={18} height={18} />],
                ].map(([key, icon]) => (
                  <div key={key} className={styles.card}>
                    <span className={styles.cardIcon}>{icon}</span>
                    <span className={styles.cardLines}>
                      <span className={styles.cardTextBar} />
                      <span className={styles.cardSubBar} />
                      <span className={styles.cardSubBar2} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {buttonType === 'inset' ? (
              <button type="button" className={styles.insetButton}>
                Action using safe-area-inset-bottom
              </button>
            ) : null}
            {buttonType === 'max' ? (
              <button type="button" className={styles.maxButton}>
                Action using safe-area-max-inset-bottom
              </button>
            ) : null}
            {buttonType === 'full' ? (
              <button type="button" className={styles.footerButton}>
                Safe action area
              </button>
            ) : null}
            {buttonType === 'chat-bad' ? (
              <button type="button" className={styles.chatButtonBad}>
                Chat
              </button>
            ) : null}
            {buttonType === 'chat-good' ? (
              <button type="button" className={styles.chatButtonGood}>
                Chat
              </button>
            ) : null}
            {buttonType === 'both' ? (
              <>
                <button type="button" className={styles.insetButtonHalf}>
                  inset
                </button>
                <button type="button" className={styles.maxButtonHalf}>
                  max-inset
                </button>
              </>
            ) : null}
          </div>
          {showOverlay && overlayMode !== 'bottom-dual' ? (
            <div className={styles.overlay}>
              <div
                className={styles.overlayTop}
                style={{ backgroundAttachment: 'fixed', backgroundImage: LAYOUTSTRIPE }}
              />
              <div
                className={styles.overlayRight}
                style={{ backgroundAttachment: 'fixed', backgroundImage: LAYOUTSTRIPE }}
              />
              <div
                className={styles.overlayBottom}
                style={{ backgroundAttachment: 'fixed', backgroundImage: LAYOUTSTRIPE }}
              />
              <div
                className={styles.overlayLeft}
                style={{ backgroundAttachment: 'fixed', backgroundImage: LAYOUTSTRIPE }}
              />
            </div>
          ) : null}
          {showOverlay && overlayMode === 'bottom-dual' ? (
            <div className={styles.overlay}>
              <div
                className={styles.overlayBottomInset}
                style={{ backgroundAttachment: 'fixed', backgroundImage: LAYOUTSTRIPE }}
              />
              <div
                className={styles.overlayBottomMax}
                style={{ backgroundAttachment: 'fixed', backgroundImage: LAYOUTSTRIPE }}
              />
            </div>
          ) : null}

          <div className={`${styles.deviceChrome} ${rotated ? styles.deviceChromeRotated : ''}`}>
            {cutoutType === 'notch' ? <div className={`${styles.cutout} ${styles.cutoutNotch}`} /> : null}
            {cutoutType === 'island' ? <div className={`${styles.cutout} ${styles.cutoutIsland}`} /> : null}
            {cutoutType === 'punch' ? <div className={`${styles.cutout} ${styles.cutoutPunch}`} /> : null}

            {showBrowserBar ? (
              <div
                className={styles.browserBar}
                style={{
                  transform: `translateY(calc(${browserBarSlide} * 100%))`,
                  transition: browserBarSnapping ? 'transform 0.3s ease' : 'transform 0.1s linear',
                }}
              >
                <div className={styles.browserBarBody} />
              </div>
            ) : null}

            {showActionBar ? (
              <div className={styles.actionBar}>
                <div className={styles.actionHandle} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export const SafeAreaInsetPlaygroundExample = () => {
  const [top, setTop] = useState(INSET_NOTCH_TOP);
  const [right, setRight] = useState(0);
  const [bottom, setBottom] = useState(DEFAULT_BOTTOM);
  const [left, setLeft] = useState(0);
  const [showOverlay, setShowOverlay] = useState(true);
  const [cutoutType, setCutoutType] = useState('notch');
  const [rotated, setRotated] = useState(false);
  const [contentScroll, setContentScroll] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [rotateKey, setRotateKey] = useState(0);
  const dragRef = useRef({ active: false, lastY: 0 });
  const phoneColRef = useRef(null);

  useEffect(() => {
    const el = phoneColRef.current;
    let cleanup = () => {};
    if (el) {
      const onWheel = (e) => {
        e.preventDefault();
        setContentScroll((prev) => Math.max(0, Math.min(CONTENTHEIGHT, prev + e.deltaY)));
      };
      el.addEventListener('wheel', onWheel, { passive: false });
      cleanup = () => {
        el.removeEventListener('wheel', onWheel);
      };
    }
    return cleanup;
  }, []);

  function handlePointerDown(e) {
    setIsDragging(true);
    dragRef.current = { active: true, lastY: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e) {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.lastY - e.clientY;
    dragRef.current.lastY = e.clientY;
    setContentScroll((prev) => Math.max(0, Math.min(CONTENTHEIGHT, prev + delta)));
  }

  function handlePointerUp() {
    dragRef.current.active = false;
    setIsDragging(false);
  }

  function handleRotate() {
    const nowRotated = !rotated;
    setRotated(nowRotated);
    if (nowRotated) {
      setTop(0);
      setRight(cutoutRightInset(cutoutType));
      setLeft(cutoutRightInset(cutoutType));
    } else {
      setTop(cutoutTopInset(cutoutType));
      setRight(0);
      setLeft(0);
    }
  }

  return (
    <div className={styles.demo}>
      <div className={styles.playgroundToolbar}>
        <span className={styles.playgroundToolbarLeft}>Cutout type</span>
        <div className={styles.playgroundToolbarRight}>
          <button
            type="button"
            className={styles.rotateButton}
            onClick={() => {
              setRotateKey((k) => k + 1);
              handleRotate();
            }}
          >
            <svg
              key={rotateKey}
              className={styles.rotateIcon}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Rotate
          </button>
        </div>
      </div>
      <div className={styles.playgroundLayout}>
        <div className={styles.playgroundControls}>
          <div className={styles.field}>
            <select
              value={cutoutType}
              onChange={(event) => {
                const val = event.target.value;
                setCutoutType(val);
                if (!rotated) {
                  setTop(cutoutTopInset(val));
                } else {
                  setRight(cutoutRightInset(val));
                }
              }}
            >
              <option value="none">none</option>
              <option value="notch">notch</option>
              <option value="island">dynamic island</option>
              <option value="punch">camera punch hole</option>
            </select>
          </div>
          <div className={styles.field}>
            <label>Top inset ({top}px)</label>
            <input type="range" min="0" max="70" value={top} onChange={(event) => setTop(Number(event.target.value))} />
          </div>
          <div className={styles.field}>
            <label>Right inset ({right}px)</label>
            <input
              type="range"
              min="0"
              max="40"
              value={right}
              onChange={(event) => setRight(Number(event.target.value))}
            />
          </div>
          <div className={styles.field}>
            <label>Bottom inset ({bottom}px)</label>
            <input
              type="range"
              min="0"
              max="60"
              value={bottom}
              onChange={(event) => setBottom(Number(event.target.value))}
            />
          </div>
          <div className={styles.field}>
            <label>Left inset ({left}px)</label>
            <input
              type="range"
              min="0"
              max="40"
              value={left}
              onChange={(event) => setLeft(Number(event.target.value))}
            />
          </div>
          <div className={styles.field}>
            <label>Overlay</label>
            <label className={styles.overlayToggle}>
              <input type="checkbox" checked={showOverlay} onChange={(event) => setShowOverlay(event.target.checked)} />
              Show safe inset areas
            </label>
          </div>
        </div>
        <div
          ref={phoneColRef}
          className={styles.playgroundPhoneCol}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <SafeAreaFrame
            title=""
            top={top}
            right={right}
            bottom={bottom}
            left={left}
            maxBottom={bottom}
            showOverlay={showOverlay}
            buttonType="full"
            cutoutType={cutoutType}
            showActionBar={true}
            rotated={rotated}
            contentScrollOffset={contentScroll}
          />
        </div>
      </div>
    </div>
  );
};

export const SafeAreaInsetVsMaxExample = () => {
  const maxBottom = ACTION_BAR_HEIGHT + ADDRESS_BAR_HEIGHT;
  const [contentScroll, setContentScroll] = useState(0);
  const [barProgress, setBarProgress] = useState(0); // 0 = visible, 1 = hidden
  const [isSnapping, setIsSnapping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [rotated, setRotated] = useState(false);
  const [rotateKey, setRotateKey] = useState(0);
  const dragRef = useRef({ active: false, lastY: 0 });
  const scrollDownAccumRef = useRef(0);
  const phoneColRef = useRef(null);
  const wheelTimerRef = useRef(null);

  const currentBottom = ACTION_BAR_HEIGHT + Math.round(ADDRESS_BAR_HEIGHT * (1 - barProgress));

  function handleRotate() {
    setRotated((r) => !r);
  }

  function snapBarIfPartial() {
    scrollDownAccumRef.current = 0;
    setIsSnapping(true);
    setBarProgress((prev) => (prev > 0 && prev < 1 ? 1 : prev));
  }

  function applyScroll(delta) {
    // delta > 0 = scroll up (content moves up, bar hides)
    // delta < 0 = scroll down (content moves down, accumulate for bar-back-in)
    if (delta > 0) {
      scrollDownAccumRef.current = 0;
      setContentScroll((prev) => Math.min(CONTENTHEIGHT, prev + delta));
      setBarProgress((prev) => Math.min(1, prev + delta / ADDRESS_BAR_HEIGHT));
    } else if (delta < 0) {
      setContentScroll((prev) => Math.max(0, prev + delta));
      scrollDownAccumRef.current += Math.abs(delta);
      if (scrollDownAccumRef.current > 20) {
        scrollDownAccumRef.current = 0;
        setIsSnapping(true);
        requestAnimationFrame(() => setBarProgress(0));
      }
    }
  }

  function handlePointerDown(e) {
    if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
    setIsSnapping(false);
    setIsDragging(true);
    dragRef.current = { active: true, lastY: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e) {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.lastY - e.clientY; // positive = drag up = scroll up
    dragRef.current.lastY = e.clientY;
    setIsSnapping(false);
    applyScroll(delta);
  }

  function handlePointerUp() {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setIsDragging(false);
    snapBarIfPartial();
  }

  useEffect(() => {
    const el = phoneColRef.current;
    let cleanup = () => {};
    if (el) {
      const onWheel = (e) => {
        e.preventDefault();
        if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
        setIsSnapping(false);
        applyScroll(e.deltaY);
        wheelTimerRef.current = setTimeout(snapBarIfPartial, 400);
      };
      el.addEventListener('wheel', onWheel, { passive: false });
      cleanup = () => {
        el.removeEventListener('wheel', onWheel);
      };
    }
    return cleanup;
  }, [rotated]);

  return (
    <div className={styles.demo}>
      <div className={styles.playgroundToolbar}>
        <span className={styles.playgroundToolbarLeft}>inset vs max-inset</span>
        <div className={styles.playgroundToolbarRight}>
          <button
            type="button"
            className={styles.rotateButton}
            onClick={() => {
              setRotateKey((k) => k + 1);
              handleRotate();
            }}
          >
            <svg
              key={rotateKey}
              className={styles.rotateIcon}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Rotate
          </button>
        </div>
      </div>
      <div className={styles.playgroundLayout}>
        <div className={styles.playgroundControls}>
          <p className={styles.hint}>
            Drag or scroll the phone to simulate the browser address bar appearing and disappearing.
          </p>
          <p className={styles.hint}>
            The <strong style={{ color: '#05a179' }}>green button</strong> uses <code>safe-area-inset-bottom</code> and
            follows the current inset. The <strong style={{ color: '#0f63d8' }}>blue button</strong> uses{' '}
            <code>safe-area-max-inset-bottom</code> and stays put.
          </p>
          <div className={styles.field}>
            <label>Overlay</label>
            <label className={styles.overlayToggle}>
              <input type="checkbox" checked={showOverlay} onChange={(event) => setShowOverlay(event.target.checked)} />
              Show safe inset areas
            </label>
          </div>
        </div>
        <div
          ref={phoneColRef}
          className={styles.playgroundPhoneCol}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <SafeAreaFrame
            title=""
            top={rotated ? 0 : 32}
            right={rotated ? INSET_ISLAND_RIGHT : 0}
            bottom={currentBottom}
            left={0}
            maxBottom={maxBottom}
            showOverlay={showOverlay}
            buttonType="both"
            topLabel="inset vs max-inset"
            cutoutType="island"
            showActionBar={true}
            showBrowserBar={true}
            browserBarSize={ADDRESS_BAR_HEIGHT}
            browserBarSlide={barProgress}
            browserBarSnapping={isSnapping}
            actionBarSize={ACTION_BAR_HEIGHT}
            overlayMode="bottom-dual"
            rotated={rotated}
            contentScrollOffset={contentScroll}
          />
        </div>
      </div>
    </div>
  );
};

export const SafeAreaChatButtonBugExample = () => {
  const [bottom, setBottom] = useState(DEFAULT_BOTTOM);
  const [right, setRight] = useState(0);
  const [top, setTop] = useState(INSET_NOTCH_TOP);
  const [offset, setOffset] = useState(10);
  const [showOverlay, setShowOverlay] = useState(true);
  const [cutoutType, setCutoutType] = useState('notch');
  const [rotated, setRotated] = useState(false);
  const [useSafeArea, setUseSafeArea] = useState(false);
  const [contentScroll, setContentScroll] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [rotateKey, setRotateKey] = useState(0);
  const dragRef = useRef({ active: false, lastY: 0 });
  const phoneColRef = useRef(null);

  useEffect(() => {
    const el = phoneColRef.current;
    let cleanup = () => {};
    if (el) {
      const onWheel = (e) => {
        e.preventDefault();
        setContentScroll((prev) => Math.max(0, Math.min(CONTENTHEIGHT, prev + e.deltaY)));
      };
      el.addEventListener('wheel', onWheel, { passive: false });
      cleanup = () => {
        el.removeEventListener('wheel', onWheel);
      };
    }
    return cleanup;
  }, []);

  function handlePointerDown(e) {
    setIsDragging(true);
    dragRef.current = { active: true, lastY: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e) {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.lastY - e.clientY;
    dragRef.current.lastY = e.clientY;
    setContentScroll((prev) => Math.max(0, Math.min(CONTENTHEIGHT, prev + delta)));
  }

  function handlePointerUp() {
    dragRef.current.active = false;
    setIsDragging(false);
  }

  function handleRotate() {
    const nowRotated = !rotated;
    setRotated(nowRotated);
    if (nowRotated) {
      setTop(0);
      setRight(cutoutRightInset(cutoutType));
    } else {
      setTop(cutoutTopInset(cutoutType));
      setRight(0);
    }
  }

  useEffect(() => {
    Prism.highlightAll();
  }, [useSafeArea, offset]);

  const codeExample = useSafeArea
    ? `.chat-button {\n  position: fixed;\n  right: calc(env(safe-area-inset-right) + ${offset}px);\n  bottom: calc(env(safe-area-inset-bottom) + ${offset}px);\n}`
    : `.chat-button {\n  position: fixed;\n  right: ${offset}px;\n  bottom: ${offset}px;\n}`;

  return (
    <div className={styles.demo}>
      <div className={styles.playgroundToolbar}>
        <span className={styles.playgroundToolbarLeft}>Cutout type</span>
        <div className={styles.playgroundToolbarRight}>
          <button
            type="button"
            className={styles.rotateButton}
            onClick={() => {
              setRotateKey((k) => k + 1);
              handleRotate();
            }}
          >
            <svg
              key={rotateKey}
              className={styles.rotateIcon}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Rotate
          </button>
        </div>
      </div>
      <div className={styles.playgroundLayout}>
        <div className={styles.playgroundControls}>
          <div className={styles.field}>
            <select
              value={cutoutType}
              onChange={(event) => {
                const val = event.target.value;
                setCutoutType(val);
                if (!rotated) {
                  setTop(cutoutTopInset(val));
                } else {
                  setRight(cutoutRightInset(val));
                }
              }}
            >
              <option value="none">none</option>
              <option value="notch">notch</option>
              <option value="island">dynamic island</option>
              <option value="punch">camera punch hole</option>
            </select>
          </div>
          <div className={styles.field}>
            <label>Bottom inset ({bottom}px)</label>
            <input
              type="range"
              min="0"
              max="70"
              value={bottom}
              onChange={(event) => setBottom(Number(event.target.value))}
            />
          </div>
          <div className={styles.field}>
            <label>Right inset ({right}px)</label>
            <input
              type="range"
              min="0"
              max="40"
              value={right}
              onChange={(event) => setRight(Number(event.target.value))}
            />
          </div>
          <div className={styles.field}>
            <label>Button offset ({offset}px)</label>
            <input
              type="range"
              min="0"
              max="40"
              value={offset}
              onChange={(event) => setOffset(Number(event.target.value))}
            />
          </div>
          <div className={styles.field}>
            <label>Overlay</label>
            <label className={styles.overlayToggle}>
              <input type="checkbox" checked={showOverlay} onChange={(event) => setShowOverlay(event.target.checked)} />
              Show safe inset areas
            </label>
          </div>
          <div className={styles.field}>
            <label>Positioning</label>
            <div className={styles.segmentedToggle}>
              <button
                type="button"
                className={`${styles.segmentedToggleBtn} ${styles.segmentedToggleBtnBuggy} ${!useSafeArea ? styles.segmentedToggleBtnActive : ''}`}
                onClick={() => setUseSafeArea(!useSafeArea)}
              >
                Fixed position
              </button>
              <button
                type="button"
                className={`${styles.segmentedToggleBtn} ${styles.segmentedToggleBtnSafe} ${useSafeArea ? styles.segmentedToggleBtnActive : ''}`}
                onClick={() => setUseSafeArea(!useSafeArea)}
              >
                Using env()
              </button>
            </div>
          </div>
        </div>
        <div
          ref={phoneColRef}
          className={styles.playgroundPhoneCol}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <SafeAreaFrame
            title=""
            top={top}
            right={right}
            bottom={bottom}
            left={0}
            maxBottom={bottom}
            showOverlay={showOverlay}
            buttonType={useSafeArea ? 'chat-good' : 'chat-bad'}
            topLabel="Bottom-right helper"
            cutoutType={cutoutType}
            showActionBar={true}
            rotated={rotated}
            chatOffset={offset}
            contentScrollOffset={contentScroll}
          />
        </div>
      </div>
      <div className={styles.codeBlockWrap}>
        <div className="gatsby-highlight">
          <pre className="language-css">
            <code>{codeExample}</code>
          </pre>
        </div>
      </div>
    </div>
  );
};

export const SafeAreaHeaderBugExample = SafeAreaChatButtonBugExample;

export const SafeAreaDefaultViewportExample = () => (
  <div style={{ margin: '2rem 0' }}>
    <SafeAreaFrame
      rotated={true}
      top={0}
      right={0}
      bottom={ACTION_BAR_HEIGHT}
      left={0}
      maxBottom={ACTION_BAR_HEIGHT}
      showOverlay={false}
      headerUsesInset={false}
      cutoutType="island"
      showActionBar={true}
      actionBarSize={ACTION_BAR_HEIGHT}
      buttonType="full"
      contentScrollable={true}
      screenStyle={{ margin: '0 44px' }}
    />
  </div>
);

export const SafeAreaViewportCoverNoEnvExample = () => (
  <div style={{ margin: '2rem 0' }}>
    <SafeAreaFrame
      rotated={true}
      top={0}
      right={0}
      bottom={0}
      left={0}
      maxBottom={0}
      showOverlay={false}
      headerUsesInset={false}
      cutoutType="island"
      showActionBar={true}
      actionBarSize={ACTION_BAR_HEIGHT}
      buttonType="full"
      contentScrollable={true}
    />
  </div>
);

export const SafeAreaViewportCoverWithEnvExample = () => (
  <div style={{ margin: '2rem 0' }}>
    <SafeAreaFrame
      rotated={true}
      top={0}
      right={INSET_ISLAND_RIGHT}
      bottom={ACTION_BAR_HEIGHT}
      left={INSET_ISLAND_RIGHT}
      maxBottom={ACTION_BAR_HEIGHT}
      showOverlay={false}
      headerUsesInset={true}
      cutoutType="island"
      showActionBar={true}
      buttonType="full"
      contentScrollable={true}
    />
  </div>
);
